<script setup>
import { computed, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  stage: {
    type: Object,
    default: null,
  },
  labels: {
    type: Array,
    default: () => [],
  },
  usedLabelIds: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['cancel', 'save']);

const name = ref('');
const labelId = ref(null);
const createLabel = ref(false);
const labelTitle = ref('');
const submitted = ref(false);

const availableLabels = computed(() => {
  return props.labels.filter(label => {
    return label.id === labelId.value || !props.usedLabelIds.includes(label.id);
  });
});

const isValid = computed(() => {
  return name.value.trim() && (createLabel.value ? labelTitle.value.trim() : labelId.value);
});

const resetForm = () => {
  name.value = props.stage?.name || '';
  labelId.value = props.stage?.label?.id || null;
  createLabel.value = false;
  labelTitle.value = '';
  submitted.value = false;
};

const submit = () => {
  submitted.value = true;
  if (!isValid.value) return;

  emit(
    'save',
    createLabel.value
      ? { name: name.value.trim(), label_title: labelTitle.value.trim() }
      : { name: name.value.trim(), label_id: labelId.value }
  );
};

watch(() => props.stage, resetForm, { immediate: true });
</script>

<template>
  <form class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak" @submit.prevent="submit">
    <div class="flex items-center justify-between gap-4">
      <h2 class="text-heading-2 text-n-slate-12">
        {{ stage ? $t('CRM.PIPELINE.EDIT_STAGE') : $t('CRM.PIPELINE.ADD_STAGE') }}
      </h2>
      <Button icon="i-lucide-x" slate sm @click.prevent="emit('cancel')" />
    </div>

    <woot-input
      v-model="name"
      :label="$t('CRM.PIPELINE.STAGE_NAME')"
      :placeholder="$t('CRM.PIPELINE.STAGE_NAME_PLACEHOLDER')"
      :error="submitted && !name ? $t('CRM.PIPELINE.STAGE_NAME_REQUIRED') : ''"
    />

    <label class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12">
      {{ $t('CRM.PIPELINE.LABEL') }}
      <div class="flex items-center gap-2 text-sm font-normal text-n-slate-11">
        <input v-model="createLabel" type="checkbox" />
        {{ $t('CRM.PIPELINE.CREATE_LABEL') }}
      </div>
      <select
        v-if="!createLabel"
        v-model.number="labelId"
        class="w-full px-3 py-2 rounded-md border bg-n-background border-n-weak text-n-slate-12"
      >
        <option :value="null">{{ $t('CRM.PIPELINE.LABEL_PLACEHOLDER') }}</option>
        <option v-for="label in availableLabels" :key="label.id" :value="label.id">
          {{ label.title }}
        </option>
      </select>
      <woot-input
        v-else
        v-model="labelTitle"
        :label="$t('CRM.PIPELINE.NEW_LABEL')"
        :placeholder="$t('CRM.PIPELINE.NEW_LABEL_PLACEHOLDER')"
        :error="submitted && !labelTitle ? $t('CRM.PIPELINE.LABEL_REQUIRED') : ''"
      />
      <span v-if="submitted && !createLabel && !labelId" class="text-sm text-n-ruby-11">
        {{ $t('CRM.PIPELINE.LABEL_REQUIRED') }}
      </span>
    </label>

    <p v-if="!labels.length && !createLabel" class="text-sm text-n-slate-11">
      {{ $t('CRM.PIPELINE.NO_LABELS') }}
    </p>

    <div class="flex justify-end gap-2">
      <Button faded slate :label="$t('CRM.PIPELINE.CANCEL')" @click.prevent="emit('cancel')" />
      <Button
        type="submit"
        :label="stage ? $t('CRM.PIPELINE.SAVE_STAGE') : $t('CRM.PIPELINE.CREATE_STAGE')"
        :disabled="!isValid || isLoading"
        :is-loading="isLoading"
      />
    </div>
  </form>
</template>
